// Visitors: people (synced to every device), starting and ending a visit, and
// importing from the phone's contacts (names and photos only).

import { useSyncExternalStore } from 'react';
import { visitTraits, sameName } from '../domain/visitors.ts';
import type { LogEvent, Reaction, Visitor } from '../domain/types.ts';
import { logEvent, updateEvent } from './actions.ts';
import { uid } from './ids.ts';
import { data } from './index.ts';

export function savePerson(p: Visitor): void {
  data().store.put('people', p);
}

export function removePerson(id: string): void {
  data().store.remove('people', id);
}

/** Someone's arrived: start a visit, or add them to the one in progress. */
export function startVisit(people: Visitor[], open?: LogEvent<'visit'>): void {
  if (!people.length) return;
  if (open) {
    const ids = [...new Set([...open.data.people, ...people.map((p) => p.id)])];
    const names = [...open.data.names, ...people.filter((p) => !open.data.people.includes(p.id)).map((p) => p.name)];
    updateEvent({ ...open, data: { ...open.data, people: ids, names } });
    return;
  }
  logEvent('visit', { people: people.map((p) => p.id), names: people.map((p) => p.name) });
}

/** They've left: close the visit, and tick off the socialisation items it counts towards. */
export function endVisit(open: LogEvent<'visit'>, reaction: Reaction | undefined, people: Visitor[]): void {
  const end = Math.max(Date.now(), open.at + 60_000);
  const done = { ...open, end, data: { ...open.data, ...(reaction ? { reaction } : {}) } };
  updateEvent(done);
  syncVisitTicks(done, people);
}

/**
 * The socialisation items a visit ticks off, kept in step with the visit:
 * after an edit (who came, how he found it, when) the old ticks go and the
 * right ones are made again. A visit with no reaction yet ticks nothing; a
 * deleted visit (`null`) takes its ticks with it.
 */
export function syncVisitTicks(visit: LogEvent<'visit'> | null, people: Visitor[], visitId = visit?.id): void {
  const { store } = data();
  for (const e of store.list('events')) if (e.type === 'social' && e.data.visitId === visitId) store.remove('events', e.id);
  if (!visit?.data.reaction) return;
  const here = people.filter((p) => visit.data.people.includes(p.id));
  for (const itemId of visitTraits(here)) logEvent('social', { itemId, reaction: visit.data.reaction, visitId: visit.id }, { at: visit.at, by: visit.by, note: `Visit from ${here.map((p) => p.name).join(', ')}` });
}

/** Log a visit that's already happened: opens the visit editor with the last hour filled in. */
export function openEarlierVisit(): void {
  const now = Date.now();
  // Lazy import: the editor imports this module.
  void import('../features/log/EventEditor.tsx').then((m) => m.openNew('visit', { people: [], names: [] }, { at: now - 60 * 60_000, end: now, title: 'Log an earlier visit' }));
}

// --- Phone contacts (Contact Picker API: Chrome on Android) ------------------------

interface ContactsManager {
  select(props: string[], opts?: { multiple?: boolean }): Promise<{ name?: string[]; icon?: Blob[] }[]>;
  getProperties?(): Promise<string[]>;
}

const contacts = (): ContactsManager | undefined => (typeof navigator !== 'undefined' && 'contacts' in navigator ? (navigator as unknown as { contacts: ContactsManager }).contacts : undefined);

export const canPickContacts = (): boolean => !!contacts() && typeof window !== 'undefined' && 'ContactsManager' in window;

/** A small square JPEG data URL from an image blob (contact photo or camera). */
export async function avatarFrom(blob: Blob, size = 112): Promise<string | undefined> {
  try {
    const bmp = await createImageBitmap(blob);
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    if (!ctx) return undefined;
    const s = Math.min(bmp.width, bmp.height);
    ctx.drawImage(bmp, (bmp.width - s) / 2, (bmp.height - s) / 2, s, s, 0, 0, size, size);
    return c.toDataURL('image/jpeg', 0.8);
  } catch {
    return undefined;
  }
}

const PENDING = 'arlo:contacts-pending';
const mark = (v: string | null) => {
  try {
    if (v) localStorage.setItem(PENDING, v);
    else localStorage.removeItem(PENDING);
  } catch {
    /* private mode: no breadcrumb */
  }
};

/**
 * Was the app reloaded while the contact picker was open (some phones reclaim
 * the page while it's in the background, and the picked contacts are lost)?
 * True once, the first time it's asked after that happened.
 */
export function contactsInterrupted(): boolean {
  try {
    const at = Number(localStorage.getItem(PENDING));
    mark(null);
    return at > 0 && Date.now() - at < 30 * 60_000;
  } catch {
    return false;
  }
}

const within = <T,>(p: Promise<T>, ms: number): Promise<T | undefined> => Promise.race([p, new Promise<undefined>((r) => setTimeout(() => r(undefined), ms))]);

/** What the last contacts import did, shown on screen (not just a toast that's gone in seconds). */
export interface ImportStatus {
  at: number;
  text: string;
  tone: 'info' | 'good' | 'bad';
  /** A different way to ask the picker, offered when it brought nobody back. */
  retry?: { label: string; opts: ImportOpts };
}
let status: ImportStatus | undefined;
const subs = new Set<() => void>();
const setStatus = (s: Omit<ImportStatus, 'at'>) => {
  status = { ...s, at: Date.now() };
  subs.forEach((f) => f());
};
/** Forget the last web-picker result (a new attempt is under way another way). */
export function clearImportStatus(): void {
  status = undefined;
  subs.forEach((f) => f());
}

export function useImportStatus(): ImportStatus | undefined {
  return useSyncExternalStore(
    (f) => (subs.add(f), () => void subs.delete(f)),
    () => status,
  );
}

/**
 * Pick people from the phone's contacts. Only names and photos are read —
 * no numbers or addresses — and anyone already in the list is skipped. Names
 * are saved the moment the picker answers; photos follow (each given a few
 * seconds), so a slow photo never holds up the names. Every step updates the
 * on-screen status. `photos: false` asks for names only.
 * Returns how many were picked, added and skipped (already there), or
 * undefined if the picker isn't available on this device.
 */
export interface ImportOpts {
  /** false: names only. */
  photos?: boolean;
  /** One person per pick (tapping them sends them straight back) — for pickers whose ticks don't work. */
  single?: boolean;
}

const MODE = 'arlo:contacts-mode';
/** How the picker last worked on this phone, so it's asked the same way next time. */
const rememberedMode = (): ImportOpts => {
  try {
    return JSON.parse(localStorage.getItem(MODE) ?? '{}') as ImportOpts;
  } catch {
    return {};
  }
};
const rememberMode = (o: ImportOpts) => {
  try {
    localStorage.setItem(MODE, JSON.stringify(o));
  } catch {
    /* fine */
  }
};

/** The next thing to try after the picker brought nobody back. */
const nextTry = (o: ImportOpts, photosOffered: boolean): ImportStatus['retry'] =>
  !o.single ? { label: 'Pick one at a time', opts: { single: true, photos: o.photos } } : o.photos !== false && photosOffered ? { label: 'One at a time, names only', opts: { single: true, photos: false } } : undefined;

export async function importContacts(existing: Visitor[], asked: ImportOpts = {}): Promise<{ picked: number; added: number; skipped: number } | undefined> {
  const opts = { ...rememberedMode(), ...asked };
  const api = contacts();
  if (!api) return undefined;
  // Photos aren't offered on every phone: ask only for what this one supports.
  const offered = (await within(contacts()?.getProperties?.() ?? Promise.resolve(undefined), 1500).catch(() => undefined)) ?? ['name'];
  const props = opts.photos === false ? ['name'] : offered;
  const wanted = props.includes('icon') ? ['name', 'icon'] : ['name'];
  setStatus({ text: 'Opening your contacts…', tone: 'info' });
  mark(String(Date.now()));
  let picked: Awaited<ReturnType<ContactsManager['select']>>;
  // Back on the page but the picker hasn't answered after a few seconds: say so.
  let stall: ReturnType<typeof setTimeout> | undefined;
  const onVisible = () => {
    if (document.visibilityState === 'visible' && !stall)
      stall = setTimeout(() => setStatus({ text: 'Waiting for your phone to send the contacts back…', tone: 'bad', retry: nextTry(opts, offered.includes('icon')) }), 5000);
  };
  document.addEventListener('visibilitychange', onVisible);
  try {
    picked = (await api.select(wanted, { multiple: !opts.single })) ?? [];
  } catch (e) {
    setStatus({ text: `Your phone said: ${(e as Error).message || (e as Error).name}`, tone: 'bad', retry: nextTry(opts, offered.includes('icon')) });
    throw e;
  } finally {
    mark(null);
    document.removeEventListener('visibilitychange', onVisible);
    clearTimeout(stall);
  }
  const added: { person: Visitor; icon?: Blob }[] = [];
  let skipped = 0;
  let nameless = 0;
  for (const c of picked) {
    const name = c.name?.find((n) => n && n.trim())?.trim();
    if (!name) {
      nameless++;
      continue;
    }
    if ([...existing, ...added.map((a) => a.person)].some((p) => sameName(p.name, name))) {
      skipped++;
      continue;
    }
    added.push({ person: { id: uid(), name, source: 'contacts', addedAt: Date.now() }, icon: c.icon?.[0] });
  }
  if (added.length) data().store.putMany('people', added.map((a) => a.person));
  if (picked.length) rememberMode({ single: opts.single, photos: opts.photos });
  const parts = [
    `Your phone sent ${picked.length} contact${picked.length === 1 ? '' : 's'}`,
    added.length ? `added ${added.length}` : '',
    skipped ? `${skipped} already here` : '',
    nameless ? `${nameless} without a name` : '',
  ].filter(Boolean);
  setStatus(
    picked.length
      ? { text: `${parts.join(' · ')}.`, tone: added.length ? 'good' : 'info' }
      : {
          text: opts.single
            ? 'Nobody came back. Tap the person’s name once — on this setting that sends them straight back.'
            : 'Nobody was chosen. Some phones won’t let you tick people on the “Share contacts” screen — pick one at a time instead: tapping a person sends them straight back.',
          tone: 'bad',
          retry: nextTry(opts, offered.includes('icon')),
        },
  );
  // Photos, one by one, after the names are safely in.
  void (async () => {
    for (const a of added) {
      if (!a.icon) continue;
      const photo = await within(avatarFrom(a.icon), 4000);
      const cur = photo && data().store.get('people', a.person.id);
      if (cur && !cur.photo) data().store.put('people', { ...cur, photo });
    }
  })();
  return { picked: picked.length, added: added.length, skipped };
}
